import { MoreVert } from '@material-ui/icons';
import LinkIcon from '@material-ui/icons/LaunchRounded';
import { useAllViewsLink } from '@monorepo/logs/src/hooks/useLogsLinks';
import { downloadViewResults } from '@monorepo/shared/apiClient/logs';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { IconButton } from '@monorepo/shared/componentsV2/Button/IconButton';
import { ButtonsGroup } from '@monorepo/shared/componentsV2/ButtonsGroup';
import { Link } from '@monorepo/shared/componentsV2/Link';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { Menu } from '@monorepo/shared/componentsV2/menus/Menu';
import { MenuButton } from '@monorepo/shared/componentsV2/menus/MenuButton';
import { SecondaryBreadcrumbToolbar } from '@monorepo/shared/componentsV2/SecondaryBreadcrumbToolbar';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { useToast } from '@monorepo/shared/contexts';
import {
  useHasProjectSettingsPermissions,
  useHasProjectUpdatePermissions,
} from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { useModal } from '@monorepo/shared/hooks/useModalV2';
import { bodySmall } from '@monorepo/shared/styles/text';

import { useDatasets } from '@monorepo/shared/hooks/datasets/useDatasets';
import { useLog } from '@monorepo/shared/hooks/logs/useLog';
import {
  getDataSourceTypeForLink,
  useDataSourceLinkOnSameOrg,
} from '@monorepo/shared/hooks/navigation/useLinkOnSame/useLinkOnSameOrg';
import { DatasetChip } from '@monorepo/workflowDataSetup/src/components/shared/DatasetChip';
import TrashIcon from '@svg/trash.svg';
import { DatasetType, QueryResponse, ViewType } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useCopyViewModal } from '../../../hooks/useCopyViewModal';
import { useEditViewInfoModal } from '../../../hooks/useEditViewInfoModal';
import { useView } from '../../../hooks/useView';
import { useViewDeleteWithConfirm } from '../../../hooks/useViewDelete';
import { ViewCustomizeColumnsMenu } from './ViewCustomizeColumnsMenu/ViewCustomizeColumnsMenu';
import { ViewIncompleteDataMessage } from './ViewIncompleteDataMessage';

const Description = styled.div`
  ${bodySmall}
  padding-top: 1rem;
  color: ${({ theme }) => theme.colors.gray666};
  white-space: pre-wrap;
`;

const StyledMenuButton = styled(MenuButton)`
  min-width: 7.5rem;
`;

const StyledDatasetChip = styled(DatasetChip)<{ $clickable?: boolean }>`
  &:hover {
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  }
`;

const DataSourceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`;

const StyledLink = styled(Link)`
  display: inline-flex;
  align-items: center;
`;

const StyledLinkIcon = styled(LinkIcon)`
  margin-left: 0.25rem;
  vertical-align: middle;
`;

export function ViewDetailToolbar({
  queryResult,
}: {
  queryResult?: QueryResponse;
}) {
  const { organizationId, projectId, viewId } = useParams();
  if (!organizationId || !projectId || !viewId) {
    throw new Error(
      "ViewDetailToolbar component was rendered in a page with url that doesn't have projectId, organizationId or viewId",
    );
  }
  const navigate = useNavigate();
  const canSubmitDataForProject = useHasProjectUpdatePermissions(projectId);
  const canEditViews = useHasProjectSettingsPermissions(projectId);
  const { openModal: openEditViewInfoModal } = useEditViewInfoModal();
  const { openModal: openCopyViewModal } = useCopyViewModal();
  const { isLoading, view } = useView({ organizationId, projectId, viewId });
  const deleter = useViewDeleteWithConfirm({
    organizationId,
    projectId,
    viewId,
    viewName: view?.name,
  });
  const allViewsPageLink = useAllViewsLink();
  const handleDelete = useCallback(async () => {
    const result = await deleter();
    if (result.success) {
      navigate(allViewsPageLink);
    }
  }, [deleter, navigate, allViewsPageLink]);
  const crumbs = useMemo(
    () => [
      { label: 'All Reports', to: allViewsPageLink },
      { label: view?.name || 'Unknown Report', to: '.' },
    ],
    [view?.name, allViewsPageLink],
  );
  const { datasets = [] } = useDatasets({
    organizationId,
    config: { useErrorBoundary: false },
  });
  const dataset = useMemo(
    () => datasets.find((d) => d.id === view?.originId),
    [datasets, view?.originId],
  );
  const { log } = useLog({
    logId: view?.type === ViewType.LOG ? view.originId : undefined,
    organizationId,
  });
  const dataSourceTypeForLink = useMemo(
    () =>
      getDataSourceTypeForLink({
        viewType: view?.type,
        datasetType: dataset?.type,
      }),
    [view, dataset?.type],
  );
  const toDataSource = useDataSourceLinkOnSameOrg({
    dataSourceType: dataSourceTypeForLink,
    dataSourceId: dataset?.originId || view?.originId,
  });

  const { confirm } = useModal();
  const { showUserFriendlyErrorToast } = useToast();

  const handleExport = useCallback(async () => {
    try {
      if (!view) {
        throw new Error("Couldn't load Report details");
      }
      await confirm({
        title: 'Export Report Data',
        description: (
          <>
            Export results for <b>{view.name}</b> Report?
          </>
        ),
        confirmButtonText: 'Export',
        afterConfirmButtonText: 'Processing...',
        onConfirmAsync: () =>
          downloadViewResults({
            viewId,
            organizationId,
            projectId,
          }),
      });
    } catch (error) {
      showUserFriendlyErrorToast(error, 'Unable to process data for export.');
    }
  }, [
    viewId,
    confirm,
    view,
    organizationId,
    projectId,
    showUserFriendlyErrorToast,
  ]);

  const handleEditDetails = useCallback(() => {
    if (!view) {
      throw new Error(
        'Trying to edit view details before loading the view - should not be possible.',
      );
    }
    openEditViewInfoModal({
      existingViewId: viewId,
      viewOriginId: view.originId,
      viewType: view.type,
    });
  }, [openEditViewInfoModal, view, viewId]);

  const handleCopy = useCallback(() => {
    if (!view) {
      throw new Error(
        'Trying to copy the view before loading the view - should not be possible.',
      );
    }
    openCopyViewModal({ existingViewId: viewId });
  }, [openCopyViewModal, view, viewId]);

  const editConfigurationLink = `${allViewsPageLink}/${viewId}/edit`;
  const handleEditConfiguration = useCallback(
    () => navigate(editConfigurationLink),
    [navigate, editConfigurationLink],
  );

  const shouldShowCustomizeColumnsButton = canEditViews;
  const shouldShowEditViewButton = canEditViews;
  const shouldShowActionMenu = canEditViews;
  const shouldShowSeparateDeleteButton = !shouldShowActionMenu && canEditViews;
  const shouldShowSeparateExportButton = !shouldShowActionMenu;

  const getDatasetChip = useCallback(
    (props: { name: string; type: DatasetType }) => (
      <StyledDatasetChip
        name={props.name}
        type={props.type}
        $clickable={canEditViews}
      />
    ),
    [canEditViews],
  );

  const datasetChipButtonGroup = useMemo(() => {
    const name = view?.type === ViewType.LOG ? log?.name : dataset?.name;
    const type = view?.type === ViewType.LOG ? DatasetType.LOG : dataset?.type;

    if (!name || !type) {
      return null;
    }

    if (canEditViews && toDataSource) {
      return (
        <StyledLink to={toDataSource}>
          {getDatasetChip({ name, type })}
          <StyledLinkIcon />
        </StyledLink>
      );
    }
    return getDatasetChip({ name, type });
  }, [
    view?.type,
    log?.name,
    dataset?.name,
    dataset?.type,
    canEditViews,
    toDataSource,
    getDatasetChip,
  ]);

  return isLoading || !view ? (
    <Loading />
  ) : (
    <SecondaryBreadcrumbToolbar
      belowBreadcrumbs={
        <>
          <DataSourceRow>
            {datasetChipButtonGroup}
            <ViewIncompleteDataMessage
              editConfigurationLink={
                canEditViews ? editConfigurationLink : undefined
              }
              view={view}
            />
          </DataSourceRow>
          {view.description && <Description>{view.description}</Description>}
        </>
      }
      crumbs={crumbs}
      rightActions={
        canSubmitDataForProject ? (
          <ButtonsGroup>
            {shouldShowSeparateExportButton && (
              <Button onClick={handleExport}>Export</Button>
            )}
            {shouldShowSeparateDeleteButton && (
              <IconButton onClick={handleDelete}>
                <TrashIcon />
                <VisuallyHidden>Delete</VisuallyHidden>
              </IconButton>
            )}
            {shouldShowCustomizeColumnsButton && (
              <ViewCustomizeColumnsMenu queryResult={queryResult} />
            )}
            {shouldShowEditViewButton && (
              <StyledMenuButton
                label="Edit Report"
                options={[
                  { onClick: handleEditDetails, displayName: 'Edit details' },
                  {
                    onClick: handleEditConfiguration,
                    displayName: 'Edit configuration',
                  },
                ]}
              />
            )}
            {shouldShowActionMenu && (
              <Menu
                options={
                  canEditViews
                    ? [
                        { onClick: handleExport, displayName: 'Export' },
                        { onClick: handleCopy, displayName: 'Copy' },
                        {
                          onClick: handleDelete,
                          displayName: 'Delete',
                          color: 'danger',
                        },
                      ]
                    : [{ onClick: handleExport, displayName: 'Export' }]
                }
                buttonElement={
                  <IconButton>
                    <MoreVert />
                    <VisuallyHidden>{`Action menu for ${view.name}`}</VisuallyHidden>
                  </IconButton>
                }
              />
            )}
          </ButtonsGroup>
        ) : null
      }
    />
  );
}
